import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import i18n from "../../../config/i18n";
import {
  getNationalLengthForCountry,
  requiresStrictNationalLength,
  sanitizePhoneDigits,
  toE164,
  trimPhoneToNationalLength,
} from "../../../utils/CountryPhoneUtils";
import { type Country } from "../../../utils/countries";
import { formatApiError } from "../../../utils/formatApiError";
import CountryCodeSelect from "../../common/CountryCodeSelect";
import OtpInput from "./OtpInput";

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const getPhoneStepSchema = () => {
  const t = (key: string) => i18n.t(key);
  return z
    .object({
      countryCode: z.string().min(2).max(3),
      phoneNumber: z.string(),
    })
    .superRefine((data, ctx) => {
      if (!requiresStrictNationalLength(data.countryCode)) return;
      const digits = sanitizePhoneDigits(data.phoneNumber);
      if (!digits) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneRequired"),
          path: ["phoneNumber"],
        });
        return;
      }
      const expected = getNationalLengthForCountry(data.countryCode);
      if (digits.length !== expected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneInvalid"),
          path: ["phoneNumber"],
        });
      }
    });
};

const getNewPasswordSchema = () => {
  const t = (key: string) => i18n.t(key);
  return z.object({
    newPassword: z.string().min(6, t("validation.passwordMin")),
  });
};

type PhoneStepData = z.infer<ReturnType<typeof getPhoneStepSchema>>;
type NewPasswordData = z.infer<ReturnType<typeof getNewPasswordSchema>>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PhoneForgotPasswordProps {
  onSendOtp: (phone: string, countryCode: string) => Promise<void>;
  onVerifyOtp: (phone: string, otp: string, countryCode: string) => Promise<void>;
  onResetPassword: (data: {
    phoneNumber: string;
    newPassword: string;
  }) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  onBack?: () => void;
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

const ErrorHelperText: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        color: "error.main",
      }}
    >
      <ErrorOutline sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ color: "error.main" }}>
        {message}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const STEPS = [
  "auth.stepPhone",
  "auth.stepVerify",
  "auth.stepNewPassword",
];

const PhoneForgotPassword: React.FC<PhoneForgotPasswordProps> = ({
  onSendOtp,
  onVerifyOtp,
  onResetPassword,
  isLoading = false,
  error = null,
  onBack,
}) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [e164Phone, setE164Phone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // --- Step 1 form ---
  const {
    control: phoneControl,
    handleSubmit: handlePhoneSubmit,
    watch: watchPhone,
    setValue: setPhoneValue,
    formState: {
      errors: phoneErrors,
      touchedFields: phoneTouched,
      isSubmitted: phoneIsSubmitted,
    },
  } = useForm<PhoneStepData>({
    resolver: zodResolver(getPhoneStepSchema()),
    defaultValues: { countryCode: "TJ", phoneNumber: "" },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const countryCode = watchPhone("countryCode");
  const maxLen = getNationalLengthForCountry(countryCode);
  const phoneErrorVisible =
    !!phoneErrors.phoneNumber &&
    (phoneTouched.phoneNumber || phoneIsSubmitted);

  // --- Step 3 form ---
  const {
    register: pwRegister,
    handleSubmit: handlePwSubmit,
    formState: { errors: pwErrors, isValid: pwIsValid },
  } = useForm<NewPasswordData>({
    resolver: zodResolver(getNewPasswordSchema()),
    defaultValues: { newPassword: "" },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const handleCountrySelect = (country: Country | null) => {
    const code = country?.code ?? "TJ";
    setPhoneValue("countryCode", code, {
      shouldDirty: true,
      shouldValidate: false,
    });
    const trimmed = trimPhoneToNationalLength(
      code,
      watchPhone("phoneNumber") || ""
    );
    setPhoneValue("phoneNumber", trimmed, {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  // Step 1 → Step 2
  const handleSendCode = async (data: PhoneStepData) => {
    const phone = toE164(data.countryCode, data.phoneNumber);
    setE164Phone(phone);
    setSendError(null);
    setIsSendingOtp(true);
    try {
      await onSendOtp(phone, data.countryCode);
      setActiveStep(1);
    } catch (err: unknown) {
      setSendError(formatApiError(err));
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 2: verify OTP with backend, then advance to step 3
  const handleOtpComplete = (code: string) => {
    setOtp(code);
    setOtpError(null);
    setIsVerifyingOtp(true);
    onVerifyOtp(e164Phone, code, countryCode)
      .then(() => setActiveStep(2))
      .catch((err: unknown) => setOtpError(formatApiError(err)))
      .finally(() => setIsVerifyingOtp(false));
  };

  const handleResendOtp = async () => {
    setSendError(null);
    setIsSendingOtp(true);
    try {
      await onSendOtp(e164Phone, countryCode);
    } catch (err: unknown) {
      setOtpError(formatApiError(err));
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 3: reset password (OTP already verified in step 2)
  const handleResetPassword = async (data: NewPasswordData) => {
    setResetError(null);
    setIsResetting(true);
    try {
      await onResetPassword({
        phoneNumber: e164Phone,
        newPassword: data.newPassword,
      });
    } catch (err: unknown) {
      setResetError(formatApiError(err));
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((key) => (
          <Step key={key}>
            <StepLabel>{t(key)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ------------------------------------------------------------------ */}
      {/* Step 1 — Phone Number                                               */}
      {/* ------------------------------------------------------------------ */}
      {activeStep === 0 && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handlePhoneSubmit(handleSendCode)();
          }}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          noValidate
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary" }}
            className="notranslate"
            translate="no"
          >
            {t("auth.forgotPasswordPhoneDesc")}
          </Typography>

          <Controller
            name="phoneNumber"
            control={phoneControl}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  label={t("auth.phoneNumber")}
                  className="notranslate"
                  translate="no"
                  fullWidth
                  required
                  autoComplete="tel-national"
                  error={phoneErrorVisible}
                  disabled={isSendingOtp || isLoading}
                  onChange={(e) => {
                    const trimmed = trimPhoneToNationalLength(
                      countryCode,
                      e.target.value || ""
                    );
                    field.onChange(trimmed);
                  }}
                  onBlur={field.onBlur}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pr: 1, borderRight: "1px solid #ccc", mr: 1 }}>
                          <img
                            loading="lazy"
                            width={20}
                            height={14}
                            src="https://flagcdn.com/w20/tj.png"
                            srcSet="https://flagcdn.com/w40/tj.png 2x"
                            alt="Tajikistan"
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>+992</Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: maxLen,
                  }}
                />
                <ErrorHelperText
                  message={
                    phoneErrorVisible
                      ? phoneErrors.phoneNumber?.message
                      : undefined
                  }
                />
              </Box>
            )}
          />

          {(sendError || error) && (
            <Alert severity="error">{sendError || error}</Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            className="notranslate"
            translate="no"
            disabled={isSendingOtp || isLoading}
            sx={{ fontWeight: 600, py: 1.2 }}
          >
            {isSendingOtp ? (
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                <CircularProgress size={20} color="inherit" />
                <span>{t("auth.sendingCode")}</span>
              </Box>
            ) : (
              t("auth.sendOtp")
            )}
          </Button>

          {onBack && (
            <Link
              component="button"
              type="button"
              className="notranslate"
              translate="no"
              onClick={onBack}
              disabled={isLoading}
              sx={{ fontSize: "0.875rem", textAlign: "center" }}
            >
              {t("auth.backToLogin")}
            </Link>
          )}
        </Box>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Step 2 — OTP Verification                                           */}
      {/* ------------------------------------------------------------------ */}
      {activeStep === 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textAlign: "center" }}
            className="notranslate"
            translate="no"
          >
            {t("auth.codeSentTo", { phone: e164Phone })}
          </Typography>

          <OtpInput
            onComplete={handleOtpComplete}
            onResend={handleResendOtp}
            error={otpError || error}
            isLoading={isLoading || isSendingOtp || isVerifyingOtp}
          />

          <Button
            variant="text"
            size="small"
            onClick={() => setActiveStep(0)}
            disabled={isLoading}
            className="notranslate"
            translate="no"
          >
            {t("auth.back")}
          </Button>
        </Box>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Step 3 — New Password                                               */}
      {/* ------------------------------------------------------------------ */}
      {activeStep === 2 && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handlePwSubmit(handleResetPassword)();
          }}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          noValidate
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary" }}
            className="notranslate"
            translate="no"
          >
            {t("auth.enterNewPassword")}
          </Typography>

          <Box>
            <TextField
              {...pwRegister("newPassword")}
              label={t("auth.newPassword")}
              className="notranslate"
              translate="no"
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              error={!!pwErrors.newPassword}
              disabled={isLoading || isResetting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      disabled={isLoading || isResetting}
                      aria-label={
                        showPassword
                          ? t("auth.hidePassword")
                          : t("auth.showPassword")
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <ErrorHelperText message={pwErrors.newPassword?.message} />
            {!pwErrors.newPassword && (
              <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5 }} className="notranslate" translate="no">
                {t("validation.passwordMinSimple")}
              </Typography>
            )}
          </Box>

          {(resetError || error) && (
            <Alert severity="error">{resetError || error}</Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            className="notranslate"
            translate="no"
            disabled={isLoading || isResetting || !pwIsValid}
            sx={{ fontWeight: 600, py: 1.2 }}
          >
            {(isLoading || isResetting) ? (
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                <CircularProgress size={20} color="inherit" />
                <span>{t("auth.resetPasswordButton")}</span>
              </Box>
            ) : (
              t("auth.resetPasswordButton")
            )}
          </Button>

          <Button
            variant="text"
            size="small"
            onClick={() => setActiveStep(1)}
            disabled={isLoading}
            className="notranslate"
            translate="no"
          >
            {t("auth.back")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PhoneForgotPassword;
